/**
 * @module ui/onboarding-message
 */
import tgamConfig from "config/tgam";
import { local } from "lib/store";
import { onboardingMessageMarkup } from "ui/templates/onboarding-message";
import logger from "utils/logger";
import PositionHelper from "utils/PositionHelper";
import { getProp } from "utils/utils";

const defaultDelay = Number(getProp(tgamConfig, "env.onboardingMessageDelay", 4)) * 1000;
const defaultLimit = Number(getProp(tgamConfig, "env.onboardingMessageLimit", 3));
const debugTag = "[ONBOARDING-MESSAGE]";

/**
 * @class
 */
export default class OnboardingMessage {
  /**
   * Set up Onboarding Message for when user has successfully triggered the button
   * @param {Object} options - options
   * @param {HTMLElement} options.targetElement - element to use as an insertion point for the Onboarding Message
   * @param {String} [options.insertPosition="beforebegin"] - insertion point (possible values: "beforebegin", "afterbegin", "beforeend", "afterend")
   * @param {String} options.id - unique ID
   * @param {String} options.message - message text
   * @param {String|null} options.localStorageItem - name of local storage item keeping track of viewed counts
   * @param {String} [options.variation=""] - theme to use (possible values: "" for default, "white", "dark" or "blue-primary")
   * @param {String} [options.messagePosition="top"] - position of Onboarding Mesage itself (possible values: "top", "middle" or "bottom")
   * @param {String} [options.arrowPosition="left"] - position of Onboarding Message arrow (possible values: "left", "right" or "center")
   * @param {Boolean} [options.staticPos=true] - whether the Onboarding Message is statically-positioned as opposed to absolutely-positioned
   * @param {Boolean} [options.collapseAfterHiding=false] - whether to visually collapse the Onboarding Message after it disappears
   * @param {Number|null} [options.delay=defaultDelay] - how long to show the message for
   * @param {Number} [options.limit=defaultLimit] - maximum number of times to show the message
   * @param {Boolean} [options.closeOnBodyClick=false] - whether to close the message upon clicking anywhere outside of boundaryContainer
   * @param {HTMLElement} [options.boundaryContainer] - boundary container for which anything outside of this is clicked, will cause the message to close
   */
  constructor({
    targetElement,
    insertPosition = "beforebegin",
    id,
    message,
    localStorageItem,
    variation,
    messagePosition = "top",
    arrowPosition = "left",
    staticPos = true,
    collapseAfterHiding = false,
    delay = defaultDelay,
    limit = defaultLimit,
    closeOnBodyClick = false,
    boundaryContainer = null
  }) {
    logger.info(`${debugTag} constructor: ${id}`);
    this.targetElement = targetElement;
    this.id = id;
    this.variation = variation;
    this.localStorageItem = localStorageItem;
    this.staticPos = staticPos;
    this.collapseAfterHiding = collapseAfterHiding;
    this.delay = delay;
    this.limit = limit;
    this.hideTimeout = 0;
    this.closeOnBodyClick = closeOnBodyClick;

    const markup = onboardingMessageMarkup(id, message, variation, messagePosition, arrowPosition, staticPos);
    targetElement.insertAdjacentHTML(insertPosition, markup);

    this.el = document.getElementById(id);
    logger.info(`${debugTag} el:`, this.el);

    if (!this.el) {
      return;
    }
    // Use the boundary container if supplied. Otherwise, default to the onboarding message
    // element as the boundary container.
    this.boundaryContainer = boundaryContainer ? boundaryContainer : this.el;

    if (!this.staticPos) {
      this.position = new PositionHelper(targetElement, this.el, "c-onboarding-message", {
        autoAdjustHorizontal: true,
        autoAdjustVertical: false
      });
    }
    const dismissButton = this.el.querySelector(".c-button");
    dismissButton && dismissButton.addEventListener("click", () => {
      this.targetElement.focus();
      this.hide();
    });

    this.el.addEventListener("transitionend", event => {
      if (event.propertyName === "opacity" && this.isHidden()) {
        if (this.collapseAfterHiding) {
          // TODO: it would be better to animate the height of the element
          // to zero via a "c-onboarding-message--collapse" class before
          // adding the "hidden" attribute, to make the experience less janky
          logger.info(`${debugTag} collapsing element:`, this.el);
          this.el.setAttribute("hidden", "hidden");
        } else {
          logger.info(`${debugTag} hiding element:`, this.el);
          this.el.setAttribute("hidden", "hidden");
        }
      }
    });

    if (this.closeOnBodyClick) {
      document.addEventListener("click", this.onBodyClick.bind(this), true);
    }
    document.addEventListener("keydown", this.onBodyKeydown.bind(this), true);
  }

  /**
   * Reveal the Onboarding Message
   */
  reveal() {
    logger.info(`${debugTag} reveal: ${this.id}`);
    if (!allowOnboardingMessage(this.localStorageItem)) {
      return;
    }
    this.el.removeAttribute("hidden");
    this.position && this.position.setPosition();
    this.el.classList.add("c-onboarding-message--open");

    if (this.localStorageItem) {
      incrementStorageCount(this.localStorageItem, this.limit);
    }
    if (!this.staticPos && this.delay !== null) {
      window.clearTimeout(this.hideTimeout);
      this.hideTimeout = window.setTimeout(() => {
        this.hide();
      }, this.delay);
    }
  }

  /**
   * Hide the Onboarding Message
   */
  hide() {
    logger.info(`${debugTag} hide: ${this.id}`);
    this.el.classList.remove("c-onboarding-message--open");
  }

  /**
   * Hide the onboarding message when the user clicks on anything outside of the
   * boundary container.
   * @param {MouseEvent} event - A click event
   */
  onBodyClick(event) {
    const target = event.target;
    const onboardingMessageIsVisible = this.el.classList.contains("c-onboarding-message--open");
    let shouldHide = false;

    if (this.boundaryContainer) {
      // Only hide if the user is clicking outside of the specified boundary container.
      shouldHide = onboardingMessageIsVisible && this.boundaryContainer !== target && !this.boundaryContainer.contains(target);
    }

    if (shouldHide) {
      this.hide();
    }
  }

  /**
   * Close the dropdown menu when the user presses the escape key
   * @param {KeyboardEvent} event - A keydown event
   */
  onBodyKeydown(event) {
    if (event.keyCode === 27 && this.isVisible()) {
      // Make sure we're focusing the correct target element, since there could
      // be multiple Onboarding Messages on the page
      if (document.activeElement.closest(`#${this.el.id}`)) {
        this.targetElement.focus();
      }
      this.hide();
    }
  }

  /**
   * Determine whether the Onboarding Message is visible
   * @returns {Boolean} Whether the Onboarding Message is currently visible
   */
  isVisible() {
    const style = window.getComputedStyle(this.el);
    const opacity = Number(style.getPropertyValue("opacity"));
    return opacity > 0;
  }

  /**
   * Determine whether the Onboarding Message is hidden
   * @returns {Boolean} Whether the Onboarding Message is currently hidden
   */
  isHidden() {
    const style = window.getComputedStyle(this.el);
    const opacity = Number(style.getPropertyValue("opacity"));
    return opacity < 1;
  }
}

/**
 * Increment the localStorage counter by 1.
 * @param {String} itemName - local storage item name
 * @param {Number} messageLimit - max number of times to show message
 */
function incrementStorageCount(itemName, messageLimit) {
  if (!itemName) {
    return;
  }
  logger.info(`${debugTag} incrementStorageCount called with ${itemName}, ${messageLimit}`);
  let count = local.get(itemName) || 0;
  const allow = count < messageLimit;
  if (allow) {
    count += 1;
    local.set(itemName, count);
  }
}

/**
 * Determine if another Onboarding Message should be displayed to user.
 * @param {String} itemName - local storage item name
 * @param {Number} [messageLimit=defaultLimit] - maximum number of times to show message
 * @returns {Boolean} true if below limit
 */
function allowOnboardingMessage(itemName, messageLimit = defaultLimit) {
  if (!itemName) {
    return true;
  }
  const count = local.get(itemName) || 0;
  const allow = count < messageLimit;
  logger.info(`${debugTag} count`, count);
  logger.info(`${debugTag} allow`, allow);
  return allow;
}
